/**
 * EMR Guard Checker Utility
 *
 * Checks all guard actions across all EMR sections before saving.
 * Guards are pre-save interceptors that can:
 * - Check conditions (e.g., "are there pending items?")
 * - Show dialogs to confirm user intent
 * - Execute API calls when user proceeds
 * - Block save if user cancels
 *
 * Single Responsibility: Guard checking and execution
 */

import axios from 'axios'
import { evaluateExpression } from './emrExpressionEvaluator'
import { applyTemplate, buildTemplateContext } from './emrTemplateEngine'
import { mentorApiURL, commonHeadersMPWeb } from 'src/mentor/services'

// Singleton state for dialog management
let dialogResolve = null
let dialogReject = null

/**
 * Check all guard actions across all EMR sections before saving
 * @param {Array} emrSections - All EMR sections with definitions
 * @param {Array} emrSectionDataList - Current EMR section data
 * @param {Object} runtime - Runtime context (patientId, eventId, etc.)
 * @returns {Promise<boolean>} - Resolves if all guards pass, rejects if user cancels
 */
export async function checkAllGuardsBeforeSave(emrSections, emrSectionDataList, runtime = {}) {
  // 1. Extract all guard actions from all sections
  const guardActions = extractGuardActions(emrSections)

  if (guardActions.length === 0) {
    return true // No guards to check
  }

  // 2. Check each guard sequentially
  for (const { guardAction, sectionCode } of guardActions) {
    const shouldShowGuard = evaluateGuardCondition(
      guardAction.guard,
      emrSectionDataList,
      sectionCode,
      runtime
    )

    if (shouldShowGuard) {
      // 3. Show dialog and wait for user response
      try {
        const formData = await showGuardDialog(guardAction.guard)

        // 4. User proceeded - call proceedApi if defined
        if (guardAction.guard.proceedApi) {
          await executeProceedApi(guardAction.guard.proceedApi, runtime, formData)
        }
      } catch (error) {
        // User cancelled - block save
        throw new Error('Guard blocked save: User cancelled')
      }
    }
  }

  return true // All guards passed
}

/**
 * Extract all guard actions from all EMR sections
 * @param {Array} emrSections - All EMR sections
 * @returns {Array} - Array of { guardAction, sectionCode }
 */
function extractGuardActions(emrSections) {
  if (!Array.isArray(emrSections)) {
    return []
  }

  const guardActions = []

  emrSections.forEach((section) => {
    const sectionDef = section?.emrSectionResponse?.section
    const actions = sectionDef?.actions || []
    const sectionCode = sectionDef?.defCode

    actions.forEach((action) => {
      if (action.kind === 'guard' && action.guard) {
        guardActions.push({
          guardAction: action,
          sectionCode: sectionCode,
        })
      }
    })
  })

  return guardActions
}

/**
 * Evaluate if guard condition is met
 * @param {Object} guardDef - Guard definition
 * @param {Array} emrSectionDataList - Current EMR section data
 * @param {string} sectionCode - Section code for this guard
 * @param {Object} runtime - Runtime context
 * @returns {boolean} - True if guard should trigger
 */
function evaluateGuardCondition(guardDef, emrSectionDataList, sectionCode, runtime) {
  if (!guardDef || !guardDef.when) {
    return false
  }

  // Build context for expression evaluation
  const sectionData = emrSectionDataList?.find((data) => data.sectionCode === sectionCode)

  // For guards that check items (e.g., "exists(items, i -> i.state == 'PENDING')")
  // we need to gather all items from all subsections
  const items = []
  if (sectionData?.subSections) {
    sectionData.subSections.forEach((subSection) => {
      if (subSection) {
        // Add state defaulting here as well
        items.push({
          ...subSection,
          state: subSection.state || 'PENDING',
        })
      }
    })
  }

  const context = {
    ...runtime,
    section: sectionData,
    items: items, // For exists() function in expressions
  }

  return evaluateExpression(guardDef.when, context)
}

/**
 * Show guard dialog and wait for user response
 * Uses custom event to trigger dialog in React component
 * @param {Object} guardDef - Guard definition with dialog config
 * @returns {Promise<Object>} - Resolves with form data if user proceeds, rejects if cancelled
 */
function showGuardDialog(guardDef) {
  return new Promise((resolve, reject) => {
    // Store resolve/reject for later use
    dialogResolve = resolve
    dialogReject = reject

    // Dispatch custom event to trigger dialog in React component
    const event = new CustomEvent('emr:show-guard-dialog', {
      detail: { guardDef },
    })
    window.dispatchEvent(event)
  })
}

/**
 * Handle user response from guard dialog
 * Called by React component when user clicks Proceed or Cancel
 * @param {boolean} proceed - Whether user proceeded or cancelled
 * @param {Object} formData - Form data if user proceeded
 */
export function handleGuardDialogResponse(proceed, formData = {}) {
  if (proceed && dialogResolve) {
    dialogResolve(formData)
  } else if (!proceed && dialogReject) {
    dialogReject(new Error('User cancelled'))
  }

  // Clear singleton state
  dialogResolve = null
  dialogReject = null
}

/**
 * Execute the guard's proceedApi call
 * @param {Object} proceedApiDef - API definition from guard
 * @param {Object} runtime - Runtime context
 * @param {Object} formData - Form data from dialog
 * @returns {Promise} - Resolves when API completes
 */
async function executeProceedApi(proceedApiDef, runtime, formData) {
  const token = localStorage.getItem('token')

  // Build request with template substitution
  const templateContext = buildTemplateContext({
    runtime,
    formData,
  })

  const url = applyTemplate(proceedApiDef.path, templateContext)
  const body = applyTemplate(proceedApiDef.body, templateContext)
  const method = (proceedApiDef.method || 'POST').toUpperCase()

  try {
    const response = await axios({
      method,
      url: `${mentorApiURL}${url}`,
      data: body,
      headers: {
        Authorization: `Bearer ${token}`,
        ...commonHeadersMPWeb,
        'ef-user-country-code': localStorage.getItem('countryCode')?.toLowerCase(),
        'ef-app-language': sessionStorage.getItem('languageCode'),
      },
    })

    console.log('[emrGuardChecker] proceedApi successful:', response.data)
    return response.data
  } catch (error) {
    console.error('[emrGuardChecker] proceedApi error:', error)
    // Don't throw - we already asked user to proceed
    // Just log the error and continue
    return null
  }
}
